import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

function Counter({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref} className="font-display text-5xl md:text-6xl lg:text-7xl text-white">
      {display}
      <span className="text-[#C96A2B]">{suffix}</span>
    </span>
  );
}

import SectionHeading from "./SectionHeading";
import { stats, company } from "../data/site";
import useDynamicContent from "../hooks/useDynamicContent";

export default function AboutSection() {
  const { content: homeContent } = useDynamicContent('home');
  const { content: aboutContent } = useDynamicContent('about');

  const title = homeContent.about_title || "12 Years of Independent Land Development & Construction.";
  const description = homeContent.about_description || "Maximize your property's value without relying on external developers. Founded in 2014 by Maheshwaram Vivek, Hoflich offers a comprehensive, 360-degree solution to help you develop your land independently. Our team is dedicated to delivering exceptional results, helping you unlock your land's full potential through architectural design, govt. approvals, project loans, site execution, and marketing & sales.";
  
  const badgesRaw = aboutContent.badges || "Design, Approvals, Project Loans, Site Execution, Marketing, Sales, Independent Build, 12 Years Exp";
  const badges = badgesRaw.split(',').map(b => b.trim()).filter(Boolean);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-28 md:py-40 px-6 md:px-12"
    >
      <div className="absolute -top-32 right-0 h-[400px] w-[400px] orange-glow opacity-50" />
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-7">
          <SectionHeading
            eyebrow="About Hoflich"
            title={title}
            description={description}
            testId="about-heading"
          />

          <div className="mt-10 flex flex-wrap gap-3 text-xs tracking-[0.18em] text-white/50">
            {badges.map(
              (t) => (
                <span
                  key={t}
                  className="px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02]"
                >
                  {t}
                </span>
              )
            )}
          </div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="grid grid-cols-2 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden"
            data-testid="about-stats-grid"
          >
            {stats.map((s) => (
              <div
                key={s.label}
                className="p-7 md:p-9 bg-[#0F0F12] hover:bg-[#15151a] transition-colors"
                data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Counter value={s.value} suffix={s.suffix} />
                <p className="mt-4 text-xs tracking-[0.22em] uppercase text-white/55">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
